import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { HelpCircle, X, BookOpen, MessageSquare, FileQuestion, ExternalLink } from 'lucide-react';

const HelpWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const quickLinks = [
    {
      icon: BookOpen,
      title: 'Help Center',
      description: 'Browse articles and guides',
      path: '/help',
      color: 'purple'
    },
    {
      icon: FileQuestion,
      title: 'FAQ',
      description: 'Quick answers to common questions',
      path: '/faq',
      color: 'blue'
    },
    {
      icon: MessageSquare,
      title: 'Contact Support',
      description: 'Get help from our team',
      path: '/support',
      color: 'green'
    }
  ];

  const handleLinkClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Help Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="fixed bottom-24 left-6 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-80 z-50 animate-in slide-in-from-bottom-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">How can we help?</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {quickLinks.map((link) => {
                const Icon = link.icon;
                const colorClasses = {
                  purple: 'bg-purple-100 text-purple-600 group-hover:bg-purple-200',
                  blue: 'bg-blue-100 text-blue-600 group-hover:bg-blue-200',
                  green: 'bg-green-100 text-green-600 group-hover:bg-green-200'
                };

                return (
                  <button
                    key={link.path}
                    onClick={() => handleLinkClick(link.path)}
                    className="group w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className={`p-2 rounded-lg ${colorClasses[link.color]} transition-colors`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 text-sm mb-0.5">
                        {link.title}
                      </div>
                      <div className="text-xs text-gray-600">
                        {link.description}
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Average response time: 24-48 hours
              </p>
            </div>
          </div>
        </>
      )}

      {/* Floating Help Button with Label - Positioned bottom-left */}
      <div className="fixed bottom-6 left-6 flex flex-col items-center z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
          aria-label="Help & Support"
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <HelpCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          )}
        </button>
        <span className="mt-2 text-xs font-semibold text-gray-700 bg-white px-3 py-1 rounded-full shadow-md whitespace-nowrap">
          Help Center
        </span>
        
        {/* Pulse animation for attention */}
        {!isOpen && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-purple-400 animate-ping opacity-20 pointer-events-none" />
        )}
      </div>
    </>
  );
};

export default HelpWidget;
